/**
 * Term-mapping across the IA output forms. The practitioner enters the agreement
 * terms once (step 7, on the 433-H or 433-D submission); step 8 pushes those
 * terms onto the other output forms (9465 / 2159 / the sibling 433) before
 * generating, so every filed form reflects the same plan. The forms use slightly
 * different `data` key vocabularies, so we normalize to a common shape and re-map.
 */

export interface CommonTerms {
  forWho: string; // T | S | J
  taxFormNo: string;
  taxPeriods: string;
  totalAmount: string;
  addTotalAmount: string;
  taxAmtPay: string;
  monthlyPayment: string;
  payDate: string; // day of month
  firstPmtDate: string;
  routing: string;
  account: string;
  noElectronic: boolean;
  payroll: boolean;
}

const str = (v: unknown, fallback = ""): string =>
  v == null ? fallback : String(v);
const bool = (v: unknown): boolean => v === true;

/** Read either editor's `data` blob into the common term shape. */
export function normalizeTerms(
  formNumber: string,
  data: Record<string, unknown>,
): CommonTerms {
  if (formNumber === "433-D") {
    return {
      forWho: str(data.file_as, "T"),
      taxFormNo: str(data.tax_forms),
      taxPeriods: str(data.tax_periods),
      totalAmount: str(data.tax_due, "0"),
      addTotalAmount: "0",
      taxAmtPay: str(data.tax_payment, "0"),
      monthlyPayment: str(data.monthly_payment, "0"),
      payDate: str(data.payment_day),
      firstPmtDate: str(data.first_pmt_date),
      routing: str(data.routing_number),
      account: str(data.account_number),
      noElectronic: bool(data.no_electronic),
      payroll: false,
    };
  }
  // 433-H / 9465 vocabulary.
  return {
    forWho: str(data.for_who, "T"),
    taxFormNo: str(data.tax_form_no),
    taxPeriods: str(data.tax_periods),
    totalAmount: str(data.total_amount, "0"),
    addTotalAmount: str(data.add_total_amount, "0"),
    taxAmtPay: str(data.tax_amt_pay, "0"),
    monthlyPayment: str(data.tax_pay_per_month, "0"),
    payDate: str(data.tax_pay_date),
    firstPmtDate: str(data.first_pmt_date),
    routing: str(data.routing_number),
    account: str(data.account_number),
    noElectronic: bool(data.no_electronic),
    payroll: bool(data.payroll_deduction),
  };
}

/** Produce the `data` patch to apply the common terms onto a target form. */
export function applyTerms(
  formNumber: string,
  t: CommonTerms,
): Record<string, unknown> {
  if (formNumber === "433-D") {
    return {
      file_as: t.forWho,
      tax_forms: t.taxFormNo,
      tax_periods: t.taxPeriods,
      tax_due: t.totalAmount,
      tax_payment: t.taxAmtPay,
      monthly_payment: t.monthlyPayment,
      payment_day: t.payDate,
      first_pmt_date: t.firstPmtDate,
      routing_number: t.routing,
      account_number: t.account,
      no_electronic: t.noElectronic,
    };
  }
  if (formNumber === "2159") {
    return {
      for_who: t.forWho,
      tax_form_no: t.taxFormNo,
      tax_periods: t.taxPeriods,
      total_amount: t.totalAmount,
      tax_pay_per_month: t.monthlyPayment,
      first_pmt_date: t.firstPmtDate,
      no_electronic: t.noElectronic,
    };
  }
  // 433-H / 9465 vocabulary.
  return {
    for_who: t.forWho,
    tax_form_no: t.taxFormNo,
    tax_periods: t.taxPeriods,
    total_amount: t.totalAmount,
    add_total_amount: t.addTotalAmount,
    tax_amt_pay: t.taxAmtPay,
    tax_pay_per_month: t.monthlyPayment,
    tax_pay_date: t.payDate,
    routing_number: t.routing,
    account_number: t.account,
    no_electronic: t.noElectronic,
    payroll_deduction: t.payroll,
  };
}
